import { Box, Loader, Text, TextButton } from '@wix/design-system';
import { Trans, useExperiments, useTranslation } from '@wix/fe-essentials/react';
import { OverviewSection, OverviewSectionSkins, RulesPanelUIProvider, useOnRuleGroupToggle } from '@wix/rules-panel-ui';
import { Confirm } from '@wix/wix-ui-icons-common';
import { useAtom, useAtomValue, useSetAtom, useStore } from 'jotai';
import React from 'react';
import { cancelHeadingStructureAIAtom, headingStructureAILoadingPageIdAtom } from '../atoms/heading-structure-ai-atoms';
import { isScanningAtom, scrollerNodeRefAtom, unsavedChangedHandlersAtom } from '../atoms/session-atoms';
import { showSaveBeforeCloseModal } from '../components/modals/show-save-before-leave-modal';
import { showStopAIHeadingStructureModal } from '../components/modals/show-stop-ai-heading-structure-modal';
import { usePanelStateAtom } from '../hooks/rules-state';
import { useA11yWizardAdapter } from '../hooks/use-a11y-wizard-adapter';
import { useBiLogger } from '../hooks/use-bi-logger';
import { dataHooks } from '../utils/datahooks';
import { renderDividedRuleLists } from './build-divided-group-component';
import { DividedIssuesList } from './divided-issues-list';
export const A11yWizardDetectedIssuesTab = _ref => {
  let {
    ruleGroups,
    issuesNumber,
    goToManualTasks
  } = _ref;
  const {
    t
  } = useTranslation();
  const {
    experiments
  } = useExperiments();
  const useNewRuleGroupList = experiments.enabled('specs.responsive-editor.RefactorGroupList');
  const {
    getPagesData
  } = usePanelStateAtom();
  const pagesData = getPagesData();
  const biLogger = useBiLogger();
  const {
    a11yWizardAdapter,
    capabilities,
    editorHost
  } = useA11yWizardAdapter();
  const compactPanelChrome = capabilities.compactPanelChrome;
  const store = useStore();
  const cancelHeadingStructureAI = useSetAtom(cancelHeadingStructureAIAtom);
  const [unsavedChangedHandlers, setUnsavedChangedHandlers] = useAtom(unsavedChangedHandlersAtom);
  const aiLoadingPageId = useAtomValue(headingStructureAILoadingPageIdAtom);
  const unsavedChangedHandlersProps = {
    navigationGuard: aiLoadingPageId ? {
      exists: true,
      onNavigationAttempt: proceed => showStopAIHeadingStructureModal({
        a11yWizardAdapter,
        editorHost,
        handleContinue: () => {},
        handleStop: () => {
          cancelHeadingStructureAI(aiLoadingPageId);
          proceed();
        },
        t
      })
    } : undefined,
    setUnsavedChanges: (exists, apply, discard) => setUnsavedChangedHandlers({
      apply,
      discard,
      exists
    }),
    showSaveBeforeLeaveModal: (handleApply, handleDiscard) => showSaveBeforeCloseModal({
      a11yWizardAdapter,
      biLogger,
      editorHost,
      handleApply,
      handleDiscard,
      t
    }),
    unsavedChanges: unsavedChangedHandlers ? unsavedChangedHandlers : {
      apply: () => Promise.resolve(),
      exists: false
    }
  };
  const toggleFunctions = useOnRuleGroupToggle({
    unsavedChangesHandlers: unsavedChangedHandlersProps
  });
  const nodeRef = useAtomValue(scrollerNodeRefAtom);
  const isScanning = useAtomValue(isScanningAtom);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    backgroundColor: "D80",
    direction: "vertical",
    gap: "SP4",
    padding: "18px 20px"
  }, /*#__PURE__*/React.createElement(OverviewSection, {
    dataHook: dataHooks.overviewSectionDetectedIssues,
    description: issuesNumber > 0 || isScanning ? t(isScanning && issuesNumber === 0 ? 'accessibility-wizard.detected-issues.overview-section.loading.description' : 'accessibility-wizard.detected-issues.overview-section.description') : /*#__PURE__*/React.createElement(Text, {
      size: "tiny",
      weight: "thin",
      secondary: true
    }, /*#__PURE__*/React.createElement(Trans, {
      components: {
        Link: /*#__PURE__*/React.createElement(TextButton, {
          size: "tiny",
          skin: "dark",
          underline: "always",
          weight: "thin",
          onClick: goToManualTasks
        })
      },
      i18nKey: "accessibility-wizard.detected-issues.overview-section.go-to-manual",
      t: t
    })),
    detectedIssues: isScanning ? issuesNumber === 0 ? /*#__PURE__*/React.createElement(Loader, {
      size: "tiny"
    }) : issuesNumber : issuesNumber === 0 ? /*#__PURE__*/React.createElement(Box, {
      color: "G10"
    }, /*#__PURE__*/React.createElement(Confirm, null)) : issuesNumber,
    showShadow: !compactPanelChrome,
    skin: issuesNumber === 0 && !isScanning ? OverviewSectionSkins.success : OverviewSectionSkins.warning,
    title: t(isScanning && issuesNumber === 0 ? 'accessibility-wizard.detected-issues.overview-section.loading.title' : issuesNumber > 0 ? 'accessibility-wizard.detected-issues.overview-section.title' : 'accessibility-wizard.detected-issues.overview-section.title.no-issues', {
      count: issuesNumber
    })
  })), /*#__PURE__*/React.createElement("div", {
    style: {
      overflow: 'visible'
    }
  }, /*#__PURE__*/React.createElement(RulesPanelUIProvider, {
    store: store,
    unsavedChangesHandlers: unsavedChangedHandlersProps,
    onToggleFunctions: toggleFunctions
  }, useNewRuleGroupList ? /*#__PURE__*/React.createElement(DividedIssuesList, {
    nodeRef: nodeRef
  }) : renderDividedRuleLists({
    nodeRef,
    pagesData,
    ruleGroups,
    softIssueListSurfaces: capabilities.wizardPresentation.softIssueListSurfaces
  }))));
};
//# sourceMappingURL=a11y-wizard-detected-issues-tab.js.map